import React, { useEffect, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import classNames from 'classnames';

import { emailValidation } from '@sv-support-sdk/utils/src/helpers/validations';
import { ICategory } from '@sv-support-sdk/shared-types/src/common/tickets';
import { ACCEPTED_DROPZONE_FILE_TYPES } from '@sv-support-sdk/utils/src/constants/attachments';
import Actions from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions';
import ActionsItem from '@sv-support-sdk/ui-components/src/components/ControlLayout/Actions/ActionsItem';
import List from '@sv-support-sdk/ui-components/src/components/Lists/List';
import Dropdown from '@sv-support-sdk/ui-components/src/components/Controls/Dropdown';
import FormGrid from '@sv-support-sdk/ui-components/src/components/ControlLayout/FormGrid';
import FormGridRow from '@sv-support-sdk/ui-components/src/components/ControlLayout/FormGrid/FormGridRow';

import Button from '../../../Controls/Button';

import Input from '../../../Controls/Input';
import Select from '../../../Controls/Select';
import Textarea from '../../../Controls/Textarea';
import DocumentTag from '../../../Controls/DocumentTag';
import FormControl from '../../../Controls/FormControl';
import Options from '../../../Lists/Options';
import MobileFooterActions from './MobileFooterActions';

import { $l } from '../../../../locales';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import api from '../../../../api';
import ticketsActions from '../../../../store/actions/tickets';
import {
  selectAttachmentsLoading,
  selectReplyAttachments,
  selectTicketCreationLoading,
} from '../../../../store/selectors/tickets';
import modalActions from '../../../../store/actions/modals';
import { CONTACT_US_MODAL } from '../../../../constants/modals';
import { WORKSPACE_STORAGE_NAME } from '../../../../constants/workspace';
import onDropHook from '../../../../hooks/onDrop';
import { selectUserEmail } from '../../../../store/selectors/user';
import { showFileDropError } from '../../../../store/actions/notifications';
import useMobileDetect from '../../../../hooks/useMobileDetect';

import css from '../style.module.scss';
import setQaAttribute from '../../../../helpers/setQaAttribute';
import {
  SUPPORT_CONTACT_US_MODAL_ADD_ATTACHMENT,
  SUPPORT_CONTACT_US_MODAL_CANCEL,
  SUPPORT_CONTACT_US_MODAL_EMAIL,
  SUPPORT_CONTACT_US_MODAL_MESSAGE,
  SUPPORT_CONTACT_US_MODAL_SUBJECT,
  SUPPORT_CONTACT_US_MODAL_SUBMIT,
  SUPPORT_CONTACT_US_MODAL_TOPIC,
} from '../../../../constants/qaAttributes';
import { SUPPORT_CES_TOPICS } from '../../../../constants/customerEffort';

interface IForm {
  email: { value: string; isTriggered: boolean };
  subject: { value: string; isTriggered: boolean };
  category: ICategory | null;
  message: { value: string; isTriggered: boolean };
}

const ContactUs = () => {
  const [form, setForm] = useState<IForm>({
    email: { value: '', isTriggered: false },
    subject: { value: '', isTriggered: false },
    category: null,
    message: { value: '', isTriggered: false },
  });
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [loaders, setLoaders] = useState({ categories: false });

  const attachmentsLoading = useAppSelector(selectAttachmentsLoading);
  const replyAttachments = useAppSelector(selectReplyAttachments);
  const userEmail = useAppSelector(selectUserEmail);
  const ticketCreationLoading = useAppSelector(selectTicketCreationLoading);
  const [onDrop] = onDropHook();
  const isMobile = useMobileDetect();

  const dispatch = useAppDispatch();

  const onInputChange = (
    key: 'email' | 'subject' | 'message',
    { target: { value } }: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setForm({ ...form, [key]: { value, isTriggered: true } });
  };

  const changeLoading = (key: string, value: boolean) => {
    setLoaders((prevState) => ({ ...prevState, [key]: value }));
  };

  const selectCategory = (category: ICategory) => {
    setForm((prevState) => ({ ...prevState, category }));
  };

  const submitTicket = async () => {
    const workspaceId = sessionStorage.getItem(WORKSPACE_STORAGE_NAME);

    await dispatch(
      ticketsActions.createTicket({
        category: form.category?.id || undefined,
        email: form.email.value,
        message: form.message.value,
        subject: form.subject.value,
        org_id: workspaceId || undefined,
        attachments: replyAttachments,
      }),
    );

    dispatch(ticketsActions.clearReplyAttachments());
  };

  const onDropRejected = (fileRejections: FileRejection[]) => {
    dispatch(showFileDropError(fileRejections));
  };

  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_DROPZONE_FILE_TYPES,
    onDropRejected,
    onDropAccepted: onDrop,
    noClick: true,
    maxSize: 10485760, // 10 MB
  });

  useEffect(() => {
    const fetchCategories = async () => {
      changeLoading('categories', true);

      try {
        const data = await api.tickets.fetchCategories();
        setCategories(data || []);
      } finally {
        changeLoading('categories', false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    setForm((state) => ({
      ...state,
      email: { value: userEmail, isTriggered: false },
    }));
  }, [userEmail]);

  const isValidForm = useMemo(() => {
    let isValid = true;

    if (
      !emailValidation(form.email.value) ||
      !form.message.value.trim() ||
      !form.subject.value.trim()
    ) {
      isValid = false;
    }

    return isValid;
  }, [form]);

  const invalidAndTriggeredFields = useMemo(
    () => ({
      email: !emailValidation(form.email.value) && form.email.isTriggered,
      subject: !form.subject.value.trim() && form.subject.isTriggered,
      message: !form.message.value.trim() && form.message.isTriggered,
    }),
    [form.subject, form.email, form.message],
  );

  return (
    <FormGrid height="full" gap="16" className="indent-md-top">
      <input {...getInputProps()} />
      <FormGridRow>
        <div
          className={classNames(css.modal__description, {
            [css[`modal__description_size_14`]]: isMobile,
          })}
        >
          {$l('CONTACT_US.TO_PROVIDE_ANSWER')}
          {userEmail ? (
            <>
              {$l('CONTACT_US.WE_SEND_EMAIL')}
              <strong>{userEmail}.</strong>
            </>
          ) : (
            $l('CONTACT_US.PROVIDE_EMAIL')
          )}
          <br />
          {$l('CONTACT_US.TIME_FOR_RESPONSE')}
          <br />
          <strong>{$l('CONTACT_US.SUPPORT_WORKING_HOURS_LABEL')}</strong>{' '}
          {$l('CONTACT_US.SUPPORT_WORKING_HOURS')}
        </div>
      </FormGridRow>
      {!userEmail && (
        <FormGridRow>
          <FormControl
            errorMessage={invalidAndTriggeredFields.email ? $l('CONTACT_US.INVALID_EMAIL') : ''}
          >
            <Input
              value={form.email.value}
              isDisabled={!!userEmail}
              isInvalid={invalidAndTriggeredFields.email}
              placeholder={$l('CONTACT_US.YOUR_EMAIL')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange('email', e)}
              {...setQaAttribute(SUPPORT_CONTACT_US_MODAL_EMAIL)}
            />
          </FormControl>
        </FormGridRow>
      )}
      <FormGridRow>
        <Dropdown
          onSelect={selectCategory}
          options={categories}
          trackBy="id"
          label="name"
          optionTextSize="md"
          listWidth="full"
          value={form.category}
          // @ts-ignore - expect error
          OptionsComponent={Options}
          render={(isOpened) => {
            return (
              <Select
                isOpened={isOpened}
                isDisabled={loaders.categories}
                value={form.category ? form.category.name : $l('CONTACT_US.SELECT_TOPIC')}
                qaId={SUPPORT_CONTACT_US_MODAL_TOPIC}
              />
            );
          }}
        />
      </FormGridRow>
      <FormGridRow>
        <FormControl
          errorMessage={invalidAndTriggeredFields.subject ? $l('CONTACT_US.INVALID_SUBJECT') : ''}
        >
          <Input
            value={form.subject.value}
            isInvalid={invalidAndTriggeredFields.subject}
            placeholder={$l('CONTACT_US.SUBJECT')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange('subject', e)}
            {...setQaAttribute(SUPPORT_CONTACT_US_MODAL_SUBJECT)}
          />
        </FormControl>
      </FormGridRow>
      <FormGridRow>
        <FormControl
          errorMessage={
            invalidAndTriggeredFields.message ? $l('CONTACT_US.INVALID_DESCRIPTION') : ''
          }
        >
          <Textarea
            height={isMobile ? '108' : '296'}
            resizableByContent
            placeholder={$l('CONTACT_US.DESCRIPTION')}
            value={form.message.value}
            isInvalid={invalidAndTriggeredFields.message}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onInputChange('message', e)}
            {...setQaAttribute(SUPPORT_CONTACT_US_MODAL_MESSAGE)}
          />
        </FormControl>
      </FormGridRow>
      {replyAttachments.length ? (
        <FormGridRow>
          <List gap="16" direction="row" isWrapped maxItemWidth="full">
            {replyAttachments.map(({ name, id, extension }) => (
              <DocumentTag
                onClose={() => dispatch(ticketsActions.removeAttachmentFromMessage(id))}
                text={name}
                extension={extension}
                key={id}
              />
            ))}
          </List>
        </FormGridRow>
      ) : null}
      <FormGridRow>
        <Button
          variant="primary-outlined"
          isDisabled={attachmentsLoading}
          onClick={open}
          icon="20--attach-file"
          iconSize="20"
          width="auto"
          padding="0-26"
          text={$l('TICKETS.ATTACH_FILE')}
          {...setQaAttribute(SUPPORT_CONTACT_US_MODAL_ADD_ATTACHMENT)}
        />
      </FormGridRow>
      <FormGridRow>
        <div className={css.modal__description}>{$l('CONTACT_US.REQUIRED_FIELDS')}</div>
      </FormGridRow>
      {isMobile ? (
        <MobileFooterActions
          isSubmitDisabled={!isValidForm || ticketCreationLoading}
          onSubmit={submitTicket}
          onClose={() => dispatch(modalActions.closeModal(CONTACT_US_MODAL))}
        />
      ) : (
        <Actions align="end" width="full">
          <ActionsItem className="indent-sm-right">
            <Button
              padding="0-26"
              variant="transparent"
              onClick={() => dispatch(modalActions.closeModal(CONTACT_US_MODAL))}
              text={$l('CONTACT_US.CANCEL')}
              {...setQaAttribute(SUPPORT_CONTACT_US_MODAL_CANCEL)}
            />
          </ActionsItem>
          <ActionsItem>
            <Button
              padding="0-26"
              isDisabled={!isValidForm || ticketCreationLoading}
              onClick={submitTicket}
              text={$l('CONTACT_US.SEND')}
              {...setQaAttribute(SUPPORT_CONTACT_US_MODAL_SUBMIT)}
            />
          </ActionsItem>
        </Actions>
      )}
    </FormGrid>
  );
};

export default ContactUs;
